import { isRenderableContinuation } from './post_approval_plaid';

export const ROUTED_TERMINAL_STATUSES = [
  'uplifted',
  'no_change',
  'identity_mismatch',
  'failed_soft',
  'timed_out',
  'unexpected_result',
];

const valueFor = (record, key) => record?.get?.(key) ?? record?.[key];

const sessionProperties = (session) => ({
  sessionType: valueFor(session, 'session_type'),
  hasShoppingCart:
    typeof session?.hasShoppingCart === 'function'
      ? session.hasShoppingCart()
      : Boolean(valueFor(session, 'shopping_cart_id')),
});

const attemptProperties = (attempt) => ({
  accountReady: valueFor(attempt, 'account_ready') === true,
  underwritingEnhanced:
    valueFor(attempt, 'company_underwriting_enhanced') === true,
});

export const processingPresentation = ({
  phase,
  status,
  error,
  pollingExhausted,
} = {}) => {
  if (phase === 'refreshing_entities') return 'refreshing';
  if (
    valueFor(error, 'reason_code') === 'unauthorized' ||
    valueFor(error, 'http_status') === 401 ||
    valueFor(valueFor(error, 'response'), 'status') === 401
  ) {
    return 'auth_recovery';
  }
  if (error || pollingExhausted || phase === 'entity_refresh_error') {
    return 'status_recovery';
  }
  if (phase === 'awaiting_server') return 'finalizing';
  if (phase === 'terminal') return 'terminal';
  if (status === 'processing' && phase === 'polling') return 'loading';

  return 'idle';
};

export const terminalContinuation = (attempt) => {
  const path = valueFor(attempt, 'next_route_path');

  if (
    attempt?.isTerminal &&
    valueFor(attempt, 'can_continue') === true &&
    isRenderableContinuation(path)
  ) {
    return { kind: 'path', path };
  }

  return { kind: 'route' };
};

export const shouldRouteOnTerminal = (
  attempt,
  routeOnTerminal = ROUTED_TERMINAL_STATUSES
) =>
  attempt?.isTerminal === true &&
  routeOnTerminal.includes(valueFor(attempt, 'status'));

export const processingViewEventProperties = ({ session, attempt }) => {
  const submittedAt = valueFor(attempt, 'submitted_at');
  const deadlineAt = valueFor(attempt, 'deadline_at');

  return {
    ...sessionProperties(session),
    attemptStatus: 'processing',
    ...attemptProperties(attempt),
    deadlineSeconds:
      submittedAt && deadlineAt
        ? Math.round((Date.parse(deadlineAt) - Date.parse(submittedAt)) / 1000)
        : null,
  };
};

export const processingOutcomeEventProperties = ({
  session,
  attempt,
  waitMs,
  pollCount,
  exhaustedRetries,
}) => {
  const status = valueFor(attempt, 'status');
  const submittedAt = valueFor(attempt, 'submitted_at');
  const submittedAtMs = submittedAt ? Date.parse(submittedAt) : NaN;

  return {
    ...sessionProperties(session),
    attemptStatus: status,
    timedOut: status === 'timed_out',
    routedVia: terminalContinuation(attempt).kind,
    waitMs: Math.round(waitMs),
    submitToTerminalMs: Number.isFinite(submittedAtMs)
      ? Math.max(0, Date.now() - submittedAtMs)
      : null,
    pollCount,
    exhaustedRetries: exhaustedRetries === true,
    ...attemptProperties(attempt),
  };
};
